/**
 * Data export (brief §11), over the real-schema domain objects.
 *
 * CSV: UTF-8 with BOM, bilingual headers ("Дата / date"), decimal point.
 * GeoJSON: Features with the plume geometry and all properties. XLSX: SheetJS,
 * loaded lazily. Column values come straight from the `plume` columns, so the
 * export mirrors what the backend stores.
 */
import type { Feature, FeatureCollection, Polygon } from "geojson";

import type { Plume } from "../data/types";
import { gradeForRate } from "../data/types";
import { SATELLITE_OPTIONS } from "../data/satellites";
import { api, normalizeSensor } from "./api";
import type { FilterValues } from "../store/filtersStore";
import type { Locale } from "../store/localeStore";
import type { PlumeGrade } from "../data/types";

export type ExportFormat = "csv" | "geojson" | "xlsx";
export type ExportArea = "extent" | "country";
export type ExportFields = "all" | "basic";

type Bounds = [[number, number], [number, number]];

interface Ctx {
  p: Plume;
  episodeId: number | null;
}

interface Column {
  /** Human heading, per interface language. */
  label: Record<Locale, string>;
  /**
   * Schema name. This is the GeoJSON property key and the second half of the
   * CSV heading, so it is part of the export's contract and never translated —
   * a script reading the file keys off this, not off the label.
   */
  field: string;
  basic: boolean;
  value: (c: Ctx) => string | number | null;
}

const COLUMNS: Column[] = [
  { label: { ru: "Дата", kk: "Күні", en: "Date" }, field: "date", basic: true, value: (c) => c.p.sensing_time.slice(0, 10) },
  { label: { ru: "Время съёмки", kk: "Түсірілім уақыты", en: "Sensing time" }, field: "sensing_time", basic: false, value: (c) => c.p.sensing_time },
  { label: { ru: "Эпизод", kk: "Эпизод", en: "Episode" }, field: "episode_id", basic: true, value: (c) => c.episodeId },
  { label: { ru: "Сенсор", kk: "Сенсор", en: "Sensor" }, field: "sensor", basic: true, value: (c) => c.p.sensor },
  { label: { ru: "Расход, кг/ч", kk: "Шығын, кг/сағ", en: "Flow rate, kg/h" }, field: "emission_rate_kg_hr", basic: true, value: (c) => c.p.emission_rate_kg_hr },
  { label: { ru: "Расход мин", kk: "Ең аз шығын", en: "Rate min" }, field: "emission_rate_low", basic: false, value: (c) => c.p.emission_rate_low },
  { label: { ru: "Расход макс", kk: "Ең көп шығын", en: "Rate max" }, field: "emission_rate_high", basic: false, value: (c) => c.p.emission_rate_high },
  { label: { ru: "Статус", kk: "Мәртебе", en: "Status" }, field: "quantification_status", basic: false, value: (c) => c.p.quantification_status },
  { label: { ru: "Категория", kk: "Санат", en: "Grade" }, field: "grade", basic: false, value: (c) => gradeForRate(c.p.emission_rate_kg_hr) },
  { label: { ru: "Уверенность (макс)", kk: "Сенімділік (макс)", en: "Confidence (max)" }, field: "max_likelihood", basic: false, value: (c) => c.p.max_likelihood },
  { label: { ru: "Уверенность (сред)", kk: "Сенімділік (орт)", en: "Confidence (mean)" }, field: "mean_likelihood", basic: false, value: (c) => c.p.mean_likelihood },
  { label: { ru: "Площадь, м²", kk: "Ауданы, м²", en: "Area, m²" }, field: "area_m2", basic: false, value: (c) => c.p.area_m2 },
  { label: { ru: "Длина плюма, м", kk: "Шлейф ұзындығы, м", en: "Plume length, m" }, field: "plume_length_m", basic: false, value: (c) => c.p.plume_length_m },
  { label: { ru: "Тип источника", kk: "Көз түрі", en: "Source type" }, field: "source_type", basic: false, value: (c) => c.p.source_type },
  { label: { ru: "Широта", kk: "Ендік", en: "Latitude" }, field: "lat", basic: false, value: (c) => c.p.locus[1] },
  { label: { ru: "Долгота", kk: "Бойлық", en: "Longitude" }, field: "lng", basic: false, value: (c) => c.p.locus[0] },
];

function columnsFor(fields: ExportFields): Column[] {
  return fields === "basic" ? COLUMNS.filter((c) => c.basic) : COLUMNS;
}

// --- filtering -------------------------------------------------------------

function matchesFilters(p: Plume, applied: FilterValues, grades: PlumeGrade[]): boolean {
  const date = p.sensing_time.slice(0, 10);
  if (grades.length && !grades.includes(gradeForRate(p.emission_rate_kg_hr))) return false;
  if (applied.from && date < applied.from) return false;
  if (applied.to && date > applied.to) return false;
  if (applied.satellites.length && !applied.satellites.includes(normalizeSensor(p.sensor))) {
    return false;
  }
  if (applied.minRate > 0 && (p.emission_rate_kg_hr === null || p.emission_rate_kg_hr < applied.minRate)) {
    return false;
  }
  return true;
}

function inBounds(p: Plume, bounds: Bounds): boolean {
  const [lng, lat] = p.locus;
  const [[w, s], [e, n]] = bounds;
  return lng >= w && lng <= e && lat >= s && lat <= n;
}

// --- serializers -----------------------------------------------------------

function csvCell(value: string | number | null): string {
  const s = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(cols: Column[], rows: Ctx[], locale: Locale): Blob {
  const header = cols.map((c) => `${c.label[locale]} / ${c.field}`).join(",");
  const body = rows.map((ctx) => cols.map((c) => csvCell(c.value(ctx))).join(",")).join("\r\n");
  return new Blob([`﻿${header}\r\n${body}\r\n`], { type: "text/csv;charset=utf-8" });
}

function toGeoJSON(cols: Column[], rows: Ctx[]): Blob {
  const features: Feature<Polygon>[] = rows.map((ctx) => {
    const properties: Record<string, unknown> = {};
    for (const c of cols) properties[c.field] = c.value(ctx);
    return { type: "Feature", geometry: ctx.p.geom, properties };
  });
  const fc: FeatureCollection<Polygon> = { type: "FeatureCollection", features };
  return new Blob([JSON.stringify(fc)], { type: "application/geo+json" });
}

async function toXlsx(cols: Column[], rows: Ctx[], locale: Locale): Promise<Blob> {
  const XLSX = await import("xlsx");
  const aoa: (string | number | null)[][] = [
    cols.map((c) => `${c.label[locale]} / ${c.field}`),
    ...rows.map((ctx) => cols.map((c) => c.value(ctx))),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "plumes");
  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

async function serialize(
  format: ExportFormat,
  cols: Column[],
  rows: Ctx[],
  locale: Locale,
): Promise<Blob> {
  if (format === "csv") return toCsv(cols, rows, locale);
  // GeoJSON carries no headings, only schema keys, so it needs no locale.
  if (format === "geojson") return toGeoJSON(cols, rows);
  return toXlsx(cols, rows, locale);
}

// --- filenames & download --------------------------------------------------

function satellitesTag(applied: FilterValues): string {
  if (!applied.satellites.length) return "all";
  return SATELLITE_OPTIONS.filter((o) => o.sats.some((s) => applied.satellites.includes(s)))
    .map((o) => o.key)
    .join("-");
}

function download(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const EXT: Record<ExportFormat, string> = { csv: "csv", geojson: "geojson", xlsx: "xlsx" };

// --- public API ------------------------------------------------------------

export interface SelectionExportOptions {
  format: ExportFormat;
  area: ExportArea;
  fields: ExportFields;
  applied: FilterValues;
  grades: PlumeGrade[];
  datasetRange: { min: string; max: string };
  bounds: Bounds | null;
  /** Language for the human half of the column headings. */
  locale: Locale;
}

/** Scenario A — export everything currently filtered (brief §11). */
export async function exportSelection(opts: SelectionExportOptions): Promise<number> {
  const [plumes, members] = await Promise.all([api.getPlumes(), api.getMembers()]);
  const episodeByPlume = new Map(members.map((m) => [m.plume_id, m.episode_id]));

  const rows: Ctx[] = plumes
    .filter((p) => matchesFilters(p, opts.applied, opts.grades))
    .filter((p) => (opts.area === "extent" && opts.bounds ? inBounds(p, opts.bounds) : true))
    .map((p) => ({ p, episodeId: episodeByPlume.get(p.plume_id) ?? null }));

  const cols = columnsFor(opts.fields);
  const blob = await serialize(opts.format, cols, rows, opts.locale);
  const from = opts.applied.from || opts.datasetRange.min;
  const to = opts.applied.to || opts.datasetRange.max;
  download(`kgs-methane_${from}_${to}_${satellitesTag(opts.applied)}.${EXT[opts.format]}`, blob);
  return rows.length;
}

/** Scenario B — export one episode with all attributes (brief §11). */
export async function exportEpisode(
  episodeId: string,
  format: ExportFormat,
  locale: Locale,
): Promise<number> {
  const data = await api.getEpisode(episodeId);
  if (!data) return 0;
  const rows: Ctx[] = data.plumes.map((p) => ({ p, episodeId: data.episode.episode_id }));
  const blob = await serialize(format, COLUMNS, rows, locale);
  download(`kgs-methane_episode-${episodeId}.${EXT[format]}`, blob);
  return rows.length;
}
